import { Component, ElementRef, OnDestroy, OnInit, ViewChild, computed, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AssistContextService } from '../../core/service/assist/assist-context.service';
import { AssistApiService, AssistPlan, AssistSource } from '../../core/service/assist/assist.service';

/** A question or an action the panel offers, grouped in the right-hand rail. */
interface Suggestion {
  label: string;
  prompt: string;
  /** Only offered while the viewer is inside one university. */
  needsUniversity?: boolean;
}

interface SuggestionGroup {
  id: string;
  title: string;
  items: Suggestion[];
}

interface Turn {
  from: 'user' | 'assist';
  text: string;
  /** Endpoints the figures came from, so an answer can be traced. */
  sources?: AssistSource[];
  /** A change request, or a question about another system: declined. */
  refused?: boolean;
  /** Answered by keyword routing because the model was unavailable. */
  degraded?: boolean;
  failed?: boolean;
}

/** One line in the history rail at the bottom. */
interface HistoryEntry {
  at: Date;
  question: string;
  intent: string;
  scope: string;
}

/**
 * Crizac Assist - a half-screen panel over the console.
 *
 * Junaid asked for the slider specifically: "yours is so small, you cannot tell
 * what is going on - that feel isn't coming. This floating is fine; on click,
 * add a slider, let a half-screen slider appear." The reference he pointed at
 * is the course finder's own assistant (course-finder-fe `/chat`), whose shape
 * this follows: conversation on the left, a suggestions rail on the right, the
 * current action stated just above the composer, and history underneath.
 *
 * It is READ-ONLY. A request that would change data is answered with the
 * approval chain it would travel, never executed - the backend module has no
 * write path at all. The confirm step below exists so the flow can be reviewed
 * now and wired to TMS later, and it says plainly that nothing was applied.
 *
 * Every figure in an answer was fetched server-side from the same endpoints the
 * on-screen widgets call, and the endpoint is printed under the answer.
 */
@Component({
  selector: 'app-crizac-assist',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatButtonModule],
  templateUrl: './crizac-assist.component.html',
  styleUrl: './crizac-assist.component.scss',
})
export class CrizacAssistComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  // Public: the template calls openPanel() on the launcher.
  readonly assistContext = inject(AssistContextService);
  private readonly api = inject(AssistApiService);
  private readonly destroyed = new Subject<void>();

  @ViewChild('composer') composer?: ElementRef<HTMLInputElement>;

  draft = '';
  busy = false;
  turns: Turn[] = [];
  history: HistoryEntry[] = [];

  /**
   * A change that has been worked out but NOT made, waiting on a named person
   * to approve it. Only ever one at a time: a queue of pending writes is a
   * thing people approve without reading.
   */
  pendingPlan: AssistPlan | null = null;
  applying = false;
  /** The result of the last applied change, so Undo stays in reach. */
  lastApplied: { summary: string; approvedBy: string | null; backupId: string | null; planId: string } | null = null;
  reverting = false;

  /** The suggestions rail. Collapses on a narrow window. */
  showIdeas = true;
  showHistory = false;

  readonly open = this.assistContext.panelOpen;
  readonly context = this.assistContext.context;

  readonly scopeLabel = computed(() => {
    const c = this.context();
    if (c.universityName && c.intakeName) return `${c.universityName} · ${c.intakeName}`;
    if (c.universityName) return c.universityName;
    if (c.universityId) return `University ${c.universityId}`;
    // No university picked yet: say which section is on screen, so the line
    // above the composer still tells the viewer what is being read.
    return this.sectionTitle(c.page);
  });

  /**
   * Which part of the console is on screen.
   *
   * The panel is mounted once in the layout and lives across navigation, so
   * without this it would keep offering a university's questions after the
   * viewer had moved on to Courses. The router is the only reliable signal -
   * each screen publishing its own page would leave the ones that forget.
   */
  private readonly SECTIONS: Record<string, string> = {
    university: 'Universities',
    courses: 'Courses',
    'course-master': 'Courses',
    'all-application': 'Applications',
    agent: 'Agents',
    reports: 'Reports',
  };

  private sectionKey(url: string): string | null {
    const parts = (url || '').split('?')[0].split('#')[0].split('/').filter(Boolean);
    const i = parts.indexOf('dashboard');
    const seg = i >= 0 ? parts[i + 1] : parts[0];
    return seg || null;
  }

  private sectionTitle(page?: string | null): string {
    if (!page) return '';
    return (
      this.SECTIONS[page] ||
      page
        .split('-')
        .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
        .join(' ')
    );
  }

  /**
   * Follows the route. Leaving the university master drops the university and
   * intake scope: keeping it would have the panel answer about Coventry while
   * the viewer is looking at the whole course list.
   */
  private applyRouteScope(url: string): void {
    const next = this.sectionKey(url);
    const current = this.context().page || null;
    if (next === current) return;

    if (next !== 'university') this.assistContext.clear();
    this.assistContext.setPage(next);

    // Only speak up if someone is watching; otherwise just move the scope.
    if (this.open() && this.turns.length) {
      const where = this.sectionTitle(next);
      this.turns.push({
        from: 'assist',
        text: where
          ? `Now on ${where} — questions cover what is on this screen.`
          : 'Scope changed with the page.',
      });
    }
    this.setIdleAction();
  }

  /**
   * The action the panel is about to take, shown directly above the composer.
   *
   * Junaid: "what action we take, that should come above the text box;
   * suggestions should always come on the right." Until a change is drafted it
   * states the scope instead, so the line is never empty.
   */
  pendingAction: { summary: string; detail: string } | null = null;

  /**
   * Quick actions along the top - the handful of things Anita does all day,
   * taken from her ticket queue: 258 closes, 225 adds, 161 opens.
   */
  readonly quickActions: Array<{ key: string; label: string; prompt: string; fill?: boolean }> = [
    // fill: put it in the box rather than send it. A change needs a country
    // named, and sending a template that has none can only come back as
    // "which countries?" - which is what it did.
    { key: 'open', label: 'Open for a country', prompt: 'Open this intake for ', fill: true },
    { key: 'close', label: 'Close for a country', prompt: 'Close this intake for ', fill: true },
    { key: 'country', label: 'Country-wise', prompt: 'Which countries is this intake open in?' },
    { key: 'status', label: 'What is open?', prompt: 'What intakes are open or closed here?' },
    { key: 'closed', label: 'What is closed?', prompt: 'Which courses are not open for this intake?' },
  ];

  /** A quick action either asks the question or starts the sentence. */
  quick(action: { prompt: string; fill?: boolean }): void {
    if (action.fill) {
      this.draft = action.prompt;
      queueMicrotask(() => this.composer?.nativeElement?.focus());
      return;
    }
    this.send(action.prompt);
  }

  /**
   * Opening the panel from anywhere seeds the greeting once.
   *
   * A field initialiser, not ngOnInit: effect() has to be created in an
   * injection context and throws if called from a lifecycle hook.
   */
  private readonly greetOnOpen = effect(() => {
    if (this.open() && !this.turns.length) this.greet();
  });

  ngOnInit(): void {
    this.restoreHistory();
    this.applyRouteScope(this.router.url);
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroyed)
      )
      .subscribe((e) => this.applyRouteScope(e.urlAfterRedirects || e.url));
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  // ------------------------------------------------------------- suggestions

  /**
   * Grouped like the course finder's rail: what you can ask about what is on
   * screen, then the wider questions. Nothing here calls the model - each item
   * is an ordinary message, sent on click.
   */
  get suggestionGroups(): SuggestionGroup[] {
    const inUniversity = Boolean(this.context().universityId);
    const inIntake = Boolean(this.context().intake);
    const page = this.context().page || null;

    const groups: SuggestionGroup[] = [];

    // The section the viewer is actually on comes first, so the rail reads
    // differently on Courses than it does on Universities.
    if (!inUniversity && page === 'courses') {
      groups.push({
        id: 'courses',
        title: 'About courses',
        items: [
          { label: 'How many courses?', prompt: 'How many courses do we have, active and inactive?' },
          { label: 'By tenant', prompt: 'How many courses does each tenant have?' },
          { label: 'By course type', prompt: 'How many courses are there of each course type?' },
        ],
      });
    }

    if (inUniversity) {
      groups.push({
        id: 'here',
        title: 'About this university',
        items: [
          { label: "What's open here?", prompt: 'What intakes are open or closed, and in how many countries?' },
          { label: 'Contract countries', prompt: 'Which countries does this university hold a contract for?' },
          { label: 'Course totals', prompt: 'How many courses does this university have, active and inactive?' },
        ],
      });
    }

    if (inIntake) {
      groups.push({
        id: 'intake',
        title: 'About this intake',
        items: [
          { label: 'Open in which countries?', prompt: 'Which countries is this intake currently open in, and how many courses each?' },
          { label: 'What is not open?', prompt: 'Which courses are not open for this intake?' },
          { label: 'Is it open in India?', prompt: 'Is this intake open for India?' },
          { label: 'Is it open in Nepal?', prompt: 'Is this intake open for Nepal?' },
        ],
      });
    }

    groups.push({
      id: 'changes',
      title: 'Changes (need approval)',
      items: [
        { label: 'Open for India', prompt: 'Open this intake for India', needsUniversity: true },
        { label: 'Open UG for India', prompt: 'Open this intake for India for all undergraduate courses', needsUniversity: true },
        { label: 'Close for India', prompt: 'Close this intake for India', needsUniversity: true },
      ].filter((s) => !s.needsUniversity || inUniversity),
    });

    groups.push({
      id: 'global',
      title: 'Across everything',
      items: [
        { label: 'Totals', prompt: 'How many universities and courses do we have, and how many are inactive?' },
        { label: 'Coventry status', prompt: 'Currently what is happening in Coventry University?' },
      ],
    });

    return groups.filter((g) => g.items.length);
  }

  // ------------------------------------------------------------------ asking

  private greet(): void {
    const scope = this.scopeLabel();
    this.turns = [
      {
        from: 'assist',
        text: scope
          ? `Asking about ${scope}. I read the same data as the statistics on screen — ask what is open, what is closed, or which countries.`
          : 'I can answer about universities, courses, intakes and the countries they are open in. Open a university and I will answer about just that one.',
      },
    ];
    this.setIdleAction();
  }

  private setIdleAction(): void {
    const scope = this.scopeLabel();
    this.pendingAction = scope
      ? { summary: 'Reading', detail: scope }
      : { summary: 'Reading', detail: 'all universities' };
  }

  send(text?: string): void {
    const q = (text ?? this.draft).trim();
    if (!q || this.busy) return;

    this.turns.push({ from: 'user', text: q });
    this.draft = '';
    this.busy = true;
    this.pendingAction = { summary: 'Working', detail: q.length > 70 ? q.slice(0, 70) + '…' : q };

    this.api
      .ask(q, this.context())
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        next: (res: any) => {
          const a = res?.data;
          this.turns.push({
            from: 'assist',
            text: a?.answer || 'No answer came back.',
            sources: a?.sources || [],
            refused: Boolean(a?.refused),
            degraded: a?.classifier === 'keywords',
          });
          this.remember(q, a?.intent || 'unknown');
          this.busy = false;

          // A change request goes on to be costed out, not answered. The
          // answer above already explains the flow; the plan says what it
          // would actually do, to this many courses, right now.
          if (a?.intent === 'write_request') {
            this.buildPlan(q);
          } else {
            this.setIdleAction();
          }
        },
        error: (err: any) => {
          this.turns.push({
            from: 'assist',
            text:
              err?.error?.message ||
              "That did not come back. The figures come from the console's own endpoints, so if the console is slow this will be too.",
            failed: true,
          });
          this.busy = false;
          this.setIdleAction();
        },
      });
  }

  // ------------------------------------------------------------------ history

  /**
   * What was asked, kept per viewer.
   *
   * Junaid: "history is getting saved in the background, at the bottom history
   * will come of who did what." Questions only: nothing here changes data, so
   * there is no applied change to record yet. Reads and writes are wrapped
   * because storage throws in a private window.
   */
  private readonly HISTORY_KEY = 'crizac-assist-history';

  private remember(question: string, intent: string): void {
    this.history.unshift({
      at: new Date(),
      question,
      intent,
      scope: this.scopeLabel() || 'All universities',
    });
    this.history = this.history.slice(0, 40);
    try {
      localStorage.setItem(this.HISTORY_KEY, JSON.stringify(this.history));
    } catch {
      // private window or blocked storage - history is just not kept
    }
  }

  private restoreHistory(): void {
    try {
      const raw = localStorage.getItem(this.HISTORY_KEY);
      if (!raw) return;
      const rows = JSON.parse(raw);
      if (Array.isArray(rows)) {
        this.history = rows
          .filter((r: any) => r?.question)
          .map((r: any) => ({ ...r, at: new Date(r.at) }));
      }
    } catch {
      // ignore - start with an empty history
    }
  }

  clearHistory(): void {
    this.history = [];
    try {
      localStorage.removeItem(this.HISTORY_KEY);
    } catch {
      // nothing to do
    }
  }

  // ------------------------------------------------------------------ panel

  close(): void {
    this.assistContext.closePanel();
  }

  clearScope(): void {
    this.assistContext.clear();
    this.turns.push({ from: 'assist', text: 'Scope cleared — questions now cover all universities.' });
    this.setIdleAction();
  }

  clearConversation(): void {
    this.turns = [];
    this.greet();
  }

  // ------------------------------------------------------------- changes

  /**
   * Asks the server what the change would do. Nothing is written by this.
   *
   * The plan comes back with a server-side id; approving sends only that id
   * back, so what is applied is exactly what was shown - the browser never
   * gets to restate the course list.
   */
  private buildPlan(question: string): void {
    this.pendingPlan = null;
    this.busy = true;
    this.pendingAction = { summary: 'Working out the change', detail: 'nothing has been written' };

    this.api
      .plan(question, this.context())
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        next: (res: any) => {
          const p: AssistPlan = res?.data || {};
          this.busy = false;
          if (!p?.ok) {
            // Not a failure - the instruction is short of something, and
            // saying which one thing is missing beats restating all of it.
            this.turns.push({ from: 'assist', text: p?.reason || 'I could not turn that into a change.' });
            this.setIdleAction();
            return;
          }
          this.pendingPlan = p;
          this.pendingAction = {
            summary: 'Waiting for your approval',
            detail: `${p.affected} ${p.affected === 1 ? 'course' : 'courses'} — nothing written yet`,
          };
        },
        error: (err: any) => {
          this.busy = false;
          this.turns.push({
            from: 'assist',
            text: err?.error?.message || 'I could not work that change out just now.',
            failed: true,
          });
          this.setIdleAction();
        },
      });
  }

  /** Approves the pending plan, in the signed-in person's name. */
  approvePlan(): void {
    const plan = this.pendingPlan;
    if (!plan?.planId || this.applying) return;

    this.applying = true;
    this.pendingAction = { summary: 'Applying', detail: plan.summary || '' };

    this.api
      .apply(plan.planId)
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        next: (res: any) => {
          const r = res?.data || {};
          this.applying = false;
          this.pendingPlan = null;
          this.lastApplied = {
            summary: r?.summary || plan.summary || 'Change applied',
            approvedBy: r?.approvedBy ?? null,
            backupId: r?.backupId ?? null,
            planId: plan.planId!,
          };
          this.turns.push({
            from: 'assist',
            text: `Done — ${r?.summary || plan.summary}. Approved by ${
              r?.approvedBy || 'you'
            }. A before-image was saved first, so this can be put back.`,
          });
          this.setIdleAction();
        },
        error: (err: any) => {
          this.applying = false;
          // The plan stays on screen: a refused apply is usually a stale plan
          // or a failed backup, and both are worth seeing beside the change.
          this.turns.push({
            from: 'assist',
            text: err?.error?.message || 'That change was not applied.',
            failed: true,
          });
        },
      });
  }

  /** Drops the plan without writing anything. */
  cancelPlan(): void {
    const plan = this.pendingPlan;
    this.pendingPlan = null;
    this.setIdleAction();
    if (plan?.planId) {
      this.api.cancel(plan.planId).pipe(takeUntil(this.destroyed)).subscribe({ error: () => {} });
    }
    this.turns.push({ from: 'assist', text: 'Dropped — nothing was changed.' });
  }

  /** Puts the last applied change back, through its backup. */
  undoLast(): void {
    const last = this.lastApplied;
    if (!last || this.reverting) return;
    this.reverting = true;

    this.api
      .revert(last.planId)
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        next: (res: any) => {
          this.reverting = false;
          this.lastApplied = null;
          this.turns.push({
            from: 'assist',
            text: `Put back — ${res?.data?.courses ?? 0} courses restored from backup ${last.backupId}.`,
          });
        },
        error: (err: any) => {
          this.reverting = false;
          this.turns.push({
            from: 'assist',
            text: err?.error?.message || 'That could not be put back.',
            failed: true,
          });
        },
      });
  }

  /** Opens the university list, so an answer can be acted on. */
  goToUniversities(): void {
    this.close();
    this.router.navigate(['/dashboard/university']);
  }
}
